import { DOCUMENT } from '@angular/common';
import { Inject, Injectable } from '@angular/core';
import { ConfigurationService } from '@sc/data-access-configuration';
import { CustomTagManagerData, Dossier } from '@sc/data-access-models';
import { DossierSandboxService } from '@sc/data-access-state-management';
import { TranslatorService } from '@sc/data-access-translation';
import { CustomerRouterService } from '@sc/feature-path-router-system';
import { DeclarationConditions } from '@sc/utils';
import { BehaviorSubject, combineLatest } from 'rxjs';
import { filter, take } from 'rxjs/operators';
import { TagManagerBase } from '../types';

const windowWithTC = window as Window & {
  tC?: {
    event?: { page?: () => void };
    container?: { reload?: (params?: Record<string, unknown>) => void };
  };
};
const windowWithTCGlobal: Window & {
  tc_events_global?: (
    target: unknown,
    eventId: string,
    values: Record<string, string | undefined>
  ) => void;
  tc_vars?: TagCommanderDataLayer;
} = window;

interface TagManagerInfos {
  [key: string]: unknown;
  event?: string;
  pageName?: string;
}

interface TagCommanderDataLayer {
  [key: string]: string;
}

interface TagCommanderPageTracking {
  pageName: string;
  statutService?: string;
  statutParcours?: string;
}

const TAG_COMMANDER_PAGE_REFERENTIAL: Record<string, TagCommanderPageTracking> =
  {
    GlassChoice: {
      pageName: 'shema-vehicule',
      statutService: 'service_engage',
      statutParcours: 'parcours_engage',
    },
    ImpactType: { pageName: 'nombre-impact' },
    ImpactSize: { pageName: 'taille-impact' },
    FieldView: { pageName: 'specificite-pb' },
    SinisterInfo: { pageName: 'resume-degat' },
    InsuranceInformation: { pageName: 'resume-degat' },
    Address: { pageName: 'adresse-repa' },
    AppointmentSearch: { pageName: 'trouver-repa' },
    FitterPage: { pageName: 'fiche-detail' },
    AppointmentValidation: { pageName: 'resume-rdv' },
    Summary: {
      pageName: 'confirmation-rdv',
      statutService: 'service_finalise',
      statutParcours: 'parcours_finalise',
    },
    CancelAppointment: { pageName: 'annulation-rdv' },
    CancelAppointmentConfirm: { pageName: 'annulation-rdv-confirmation' },
    ModifyAppointment: { pageName: 'modification-rdv' },
    ModifyAppointmentConfirm: { pageName: 'modification-rdv-confirmation' },
    PopinCreneau: { pageName: 'popin-creneau' },
    PopinCoordonees: { pageName: 'popin-coordonees' },
  };

@Injectable({
  providedIn: 'root',
})
export class CustomTagManagerService extends TagManagerBase {
  private static readonly customRessourceLoaded = new BehaviorSubject<boolean>(
    false
  );
  private readonly customTagConfig: CustomTagManagerData | undefined;

  private readonly isTagCommanderSdkUsed: boolean;
  private readonly isTagCommanderBufferUsed: boolean;
  constructor(
    private readonly configService: ConfigurationService,
    @Inject(DOCUMENT) private readonly doc: Document,
    private readonly cutomerRouter: CustomerRouterService,
    private readonly translator: TranslatorService,
    private readonly dossierSB: DossierSandboxService,
    private readonly declarationConditions: DeclarationConditions
  ) {
    super();
    this.isTagCommanderSdkUsed = this.configService
      .getInsurerFeatures()
      ?.includes('tag-commander-sdk-tc');
    this.isTagCommanderBufferUsed = this.configService
      .getInsurerFeatures()
      ?.includes('tag-commander-global-buffer');
    this.customTagConfig = this.configService.getConfig().customTagManagerData;

    if (this.isTagCommanderSdkUsed) {
      this.initRessource();
    } else if (this.isTagCommanderBufferUsed) {
      this.addVarScriptingTag();
    }
  }

  private afterRessourceLoaded() {
    return CustomTagManagerService.customRessourceLoaded.pipe(
      filter((isLoaded) => !!isLoaded),
      take(1)
    );
  }

  private initRessource() {
    if (!this.usable()) {
      return;
    }

    CustomTagManagerService.customRessourceLoaded
      .pipe(
        filter((isLoaded) => !isLoaded),
        take(1)
      )
      .subscribe(() => {
        if (this.customTagConfig?.resources?.length === 1) {
          this.addJSScriptTag(
            this.configService.getId(),
            this.customTagConfig.resources[0].url,
            0
          );
        } else {
          this.customTagConfig?.resources?.forEach((resource, index) => {
            this.addJSScriptTag(
              this.configService.getId(),
              resource.url,
              index
            );
          });
        }
      });
  }

  usable() {
    return this.customTagConfig;
  }

  pushTag(infos: TagManagerInfos) {
    if (!this.usable()) {
      return;
    }

    const pageUrl = typeof infos?.pageName === 'string' ? infos.pageName : null;

    combineLatest([
      this.afterRessourceLoaded(),
      this.cutomerRouter?.getPageFromUrl(pageUrl),
    ])
      ?.pipe(take(1))
      ?.subscribe(([, page]) => {
        // Hard coded for each config.
        // Better than the usage of "eval" method which is unsecure and have bad perf.

        if (this.isTagCommanderSdkUsed) {
          windowWithTC.tC?.event?.page?.();
        } else if (this.isTagCommanderBufferUsed) {
          if (infos.event === 'page' && pageUrl && !!page) {
            if (page.name === 'Summary') {
              this.dossierSB.dossier$
                .pipe(
                  filter((dossier) => !!dossier),
                  take(1)
                )
                .subscribe((dossier) => this.pushPageTag(page.name, dossier));
            } else {
              this.pushPageTag(page.name);
            }
          } else if (infos.event) {
            windowWithTCGlobal?.tc_events_global?.(this, 'click', {
              evt_click_name: infos.event,
            });
          } else if (infos?.isPopIn) {
            this.pushPageTag(infos.pageName as string);
          }
        }
      });
  }

  addJSScriptTag(id: string, scriptUrl: string, index: number) {
    const customRessourceScript = this.doc.createElement('script');
    const idTag = id + '_customscript_' + index;
    customRessourceScript.id = idTag;
    customRessourceScript.async = true;
    customRessourceScript.src = scriptUrl;
    customRessourceScript.type = 'text/javascript';

    customRessourceScript.addEventListener('load', () =>
      CustomTagManagerService.customRessourceLoaded.next(true)
    );
    customRessourceScript.addEventListener('error', (err) =>
      console.error(
        'Failed To Load Custom Tracker Ressource with config :',
        this.customTagConfig,
        err
      )
    );

    customRessourceScript.setAttribute('nonce', 'unsafe-inline');

    if (this.doc.getElementById(idTag) === null) {
      const previousContainer = this.doc.getElementById(
        id + '_customscript_' + (index - 1)
      );
      const dataLayerScript = this.doc.getElementById(this.getCustomTagId());
      const previousScript = previousContainer || dataLayerScript;

      if (previousScript) {
        previousScript.parentNode?.insertBefore(
          customRessourceScript,
          previousScript.nextSibling
        );
      } else {
        this.doc.head.insertBefore(
          customRessourceScript,
          this.doc.head.firstChild
        );
      }
    }
  }

  addVarScriptingTag() {
    if (this.isTagCommanderBufferUsed) {
      const tagId = this.getCustomTagId();

      if (this.doc.getElementById(tagId) === null) {
        const customRessourceScript = this.changeTcVar();
        if (customRessourceScript) {
          this.doc.head.insertBefore(
            customRessourceScript,
            this.doc.head.firstChild
          );
        }
      } else if (!windowWithTCGlobal?.tc_vars) {
        this.changeTcVar();
      }
      this.initRessource(); // For maif the javascript ressource need to be loaded after the tc_vars initialization
    }
  }

  changeTcVar(
    pageName?: string,
    statutServiceValue?: string,
    statutParcoursValue?: string
  ) {
    if (this.isTagCommanderBufferUsed) {
      const tagId = this.getCustomTagId();
      let existingScriptTag = this.doc.getElementById(
        tagId
      ) as HTMLScriptElement;
      if (!existingScriptTag) {
        existingScriptTag = this.doc.createElement('script');
        existingScriptTag.id = tagId;
        existingScriptTag.type = 'text/javascript';
      }
      const datalayer: TagCommanderDataLayer = {
        env_template: 'espaceperso',
        env_work: this.getEnvName(),
        env_xtn2: '119',
        user_id: '',
        profil: '1',
        identification_type: '',
        page_name: '',
        page_cat1: 'particuliers',
        page_cat2: 'declarationdesinistre',
        page_cat3: 'auto-moto',
        page_cat4: 'bev',
        page_cat5: '',
        page_cat6: '',
        page_cat7: 'aari',
        search_keywords: '',
        search_page_nb: '',
        type_page_4: 'selfcare',
        statut_service: '',
        statut_parcours: '',
      };
      if (statutServiceValue) {
        datalayer['statut_service'] = statutServiceValue;
      }
      if (statutParcoursValue) {
        datalayer['statut_parcours'] = statutParcoursValue;
      }
      if (pageName) {
        datalayer['page_name'] = pageName;
      }
      if (windowWithTCGlobal.tc_vars) {
        Object.assign(windowWithTCGlobal.tc_vars, datalayer);
      } else {
        windowWithTCGlobal.tc_vars = datalayer;
      }
      existingScriptTag.text = `var tc_vars=${JSON.stringify(datalayer)};`;
      return existingScriptTag;
    }
  }

  private pushPageTag(pageName: string, dossier?: Dossier) {
    const pageTracking = this.getPageTracking(pageName, dossier);

    this.changeTcVar(
      pageTracking.pageName,
      pageTracking.statutService,
      pageTracking.statutParcours
    );
    // Force TC to re-read tc_vars into its internal data layer before firing the event.
    windowWithTC.tC?.container?.reload?.({});
    windowWithTCGlobal.tc_events_global?.(this, 'pageVirtuelle', {
      evt_page_name: pageTracking.pageName,
    });
  }

  private getPageTracking(
    pageName: string,
    dossier?: Dossier
  ): TagCommanderPageTracking {
    if (pageName === 'Summary' && dossier) {
      return this.getSummaryPageTracking(dossier);
    }

    return (
      TAG_COMMANDER_PAGE_REFERENTIAL[pageName] || {
        pageName: this.translator.getInstantTranslation(
          'GlobalApplicativeConstants.tabTitles.pageTitles.tagCommander.' +
            pageName
        ),
      }
    );
  }

  private getSummaryPageTracking(dossier: Dossier): TagCommanderPageTracking {
    const tracking = TAG_COMMANDER_PAGE_REFERENTIAL.Summary;

    if (this.declarationConditions.isGlassDeclarationEndType(dossier)) {
      return {
        ...tracking,
        pageName: 'bris-de-glace-specifique',
      };
    }

    if (this.declarationConditions.hasRequestedAppointment(dossier)) {
      return {
        ...tracking,
        pageName: 'mail-avec-coordonnees-envoye',
      };
    }

    return tracking;
  }

  getEnvName() {
    return this.customTagConfig?.resources?.some(
      (res) => res?.env?.includes('green') || res?.env?.includes('blue')
    )
      ? 'prod'
      : 'preprod';
  }

  getCustomTagId() {
    return this.configService.getId() + '_custom_var_script';
  }

  pushClickEvent(eventValue?: string) {
    if (this.isTagCommanderBufferUsed) {
      windowWithTCGlobal.tc_events_global?.(this, 'click', {
        evt_click_name: eventValue,
      });
    }
  }
}
